// Eagerly import zod to avoid TDZ (Temporal Dead Zone) errors.
// Zod has circular dependencies that can cause initialization issues
// when loaded asynchronously in chunks. Importing it here ensures it's
// initialized before any other code that uses it.
import 'zod';

import React from 'react';
import { createRoot } from 'react-dom/client';
import { App } from '../App';
import '../global.css';

// ─── Auto-reload on stale chunk errors ──────────────────────────────
// When a deploy ships new bundles, browsers with cached index.html may
// still reference old chunk hashes that no longer exist. Detect those
// errors and reload to fetch the current index.html (and new chunks).
const STALE_CHUNK_RELOAD_KEY = '__staleChunkReloadAt';
const RELOAD_COOLDOWN_MS = 30_000; // Don't reload-loop more than once per 30s

function isStaleChunkError(message: string): boolean {
  if (!message) return false;
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    /ChunkLoadError/i.test(message) ||
    (message.includes('Loading chunk') && message.includes('failed'))
  );
}

function maybeReloadForStaleChunk(message: string) {
  if (!isStaleChunkError(message)) return;
  try {
    const last = parseInt(sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY) || '0', 10);
    if (Date.now() - last < RELOAD_COOLDOWN_MS) return; // already reloaded recently
    sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {}
  // Force a hard reload bypassing cache
  window.location.reload();
}

window.addEventListener('error', (event) => {
  const msg = event?.message || (event?.error as Error)?.message || '';
  maybeReloadForStaleChunk(msg);
});

window.addEventListener('unhandledrejection', (event) => {
  const msg = (event?.reason as any)?.message || String(event?.reason || '');
  maybeReloadForStaleChunk(msg);
});

createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
